import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Section, Empty } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import PhotoUpload from "@/components/PhotoUpload";
import HistoryButton from "@/components/HistoryButton";
import ExportImport from "@/components/ExportImport";

export default function InventoryMovements() {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "masuk" });
  const [viewPhoto, setViewPhoto] = useState(null);

  const load = async () => {
    const [m, s, inv] = await Promise.all([
      api.get("/inventory-movements"),
      api.get("/sites"),
      api.get("/inventory"),
    ]);
    setItems(m.data); setSites(s.data); setInventory(inv.data);
  };
  useEffect(() => { load(); }, []);

  // When user picks inventory item, autofill name + unit
  const pickItem = (code) => {
    const it = inventory.find((i) => i.code === code);
    setForm({
      ...form,
      item_code: code,
      item_name: it?.name || "",
      unit: it?.unit || "pcs",
      site_id: form.site_id || it?.site_id || "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/inventory-movements", { ...form, qty: Number(form.qty || 0) });
      toast.success("Pergerakan tersimpan");
      setForm({ type: "masuk" }); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        title="Barang Masuk / Keluar"
        subtitle="Catat pergerakan barang dengan foto bukti"
        testId="movements-header"
        actions={
          <>
          <ExportImport module="inventory-movements" canImport={false} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="movement-add-btn" className="rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold"><Plus size={16} className="mr-1" /> Catat</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-neutral-800 rounded-none max-w-2xl">
              <DialogHeader><DialogTitle className="font-display font-black text-2xl">Catat Barang Masuk/Keluar</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-xs font-mono uppercase text-neutral-400">Tipe</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" data-testid="mov-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                      <SelectItem value="masuk">Masuk</SelectItem>
                      <SelectItem value="keluar">Keluar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-mono uppercase text-neutral-400">Tanggal</Label>
                  <Input type="date" required value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" /></div>
                <div><Label className="text-xs font-mono uppercase text-neutral-400">Barang</Label>
                  <Select value={form.item_code || ""} onValueChange={pickItem}>
                    <SelectTrigger className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" data-testid="mov-item-code">
                      <SelectValue placeholder="Pilih barang dari inventory..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                      {inventory.length === 0 ? (
                        <SelectItem value="__none__" disabled>Belum ada barang di inventory</SelectItem>
                      ) : inventory.map((i) => (
                        <SelectItem key={i.id} value={i.code}>
                          {i.code} — {i.name} (stok: {i.stock || 0} {i.unit || "pcs"})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-mono uppercase text-neutral-400">Nama Barang</Label>
                  <Input value={form.item_name || ""} readOnly className="mt-1 rounded-none bg-neutral-950 border-neutral-800 h-11 text-neutral-500" /></div>
                <div><Label className="text-xs font-mono uppercase text-neutral-400">Jumlah</Label>
                  <Input type="number" required value={form.qty ?? ""} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" data-testid="mov-qty" /></div>
                <div><Label className="text-xs font-mono uppercase text-neutral-400">Satuan</Label>
                  <Input value={form.unit || ""} readOnly className="mt-1 rounded-none bg-neutral-950 border-neutral-800 h-11 text-neutral-500" /></div>
                <div className="md:col-span-2"><Label className="text-xs font-mono uppercase text-neutral-400">Site</Label>
                  <Select value={form.site_id || ""} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                    <SelectTrigger className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                      {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2"><Label className="text-xs font-mono uppercase text-neutral-400">Catatan</Label>
                  <Input value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1 rounded-none bg-neutral-900 border-neutral-800 h-11" /></div>
                <div className="md:col-span-2">
                  <PhotoUpload value={form.photo_url} onChange={(u) => setForm({ ...form, photo_url: u })} kind="movement" label="Foto Bukti (wajib)" />
                </div>
                <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none border-neutral-800">Batal</Button>
                  <Button type="submit" className="rounded-none bg-[#FF6600] hover:bg-[#ff7a1a] text-black font-bold">Simpan</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </>
        }
      />
      <Section>
        {items.length === 0 ? <Empty /> : (
          <div className="border border-neutral-800 bg-[#141414]">
            <Table>
              <TableHeader>
                <TableRow className="border-neutral-800 hover:bg-transparent">
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Tanggal</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Tipe</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Kode</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Nama</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Qty</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Stok Setelah</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Site</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Foto</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((m) => (
                  <TableRow key={m.id} className="border-neutral-800">
                    <TableCell className="font-mono text-xs">{m.date}</TableCell>
                    <TableCell>
                      <span className={`text-xs font-mono uppercase px-2 py-1 border ${m.type === "masuk" ? "border-[#16A34A] text-[#16A34A]" : "border-[#FF6600] text-[#FF6600]"}`}>{m.type}</span>
                    </TableCell>
                    <TableCell className="font-mono">{m.item_code}</TableCell>
                    <TableCell className="font-mono text-xs">{m.item_name}</TableCell>
                    <TableCell className="font-mono font-bold">{m.qty} {m.unit || ""}</TableCell>
                    <TableCell className="font-mono text-xs text-[#FFD700]">{m.stock_after ?? "-"}</TableCell>
                    <TableCell className="font-mono text-xs">{sites.find((s) => s.id === m.site_id)?.name || "-"}</TableCell>
                    <TableCell>
                      {m.photo_url ? <button onClick={() => setViewPhoto(m.photo_url)} className="text-[#FF6600]"><ImageIcon size={16} /></button> : <span className="text-neutral-600 text-xs">-</span>}
                    </TableCell>
                    <TableCell><HistoryButton entityType="inventory_movements" entityId={m.id} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      <Dialog open={!!viewPhoto} onOpenChange={(o) => !o && setViewPhoto(null)}>
        <DialogContent className="bg-[#0A0A0A] border-neutral-800 rounded-none max-w-xl">
          <DialogHeader><DialogTitle className="font-display font-black">Foto Bukti</DialogTitle></DialogHeader>
          {viewPhoto && <img src={`${process.env.REACT_APP_BACKEND_URL}${viewPhoto}?auth=${localStorage.getItem("erp_token") || ""}`} alt="bukti" className="w-full border border-neutral-800" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
